import { useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  ArrowUpRight,
  Calendar,
  CheckCircle2,
  Clock,
  Lock,
  Mail,
  Send,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { paths } from 'src/routes/paths'
import {
  BOOKING_URL,
  CONTACT_EMAIL,
} from 'src/pages/Home/components/BookMeetingSection/consts'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Input } from 'src/shared/components/ui/input'
import { Textarea } from 'src/shared/components/ui/textarea'
import { cn } from 'src/shared/components/ui/utils'
import { useContact } from 'src/api/server/useContact'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { Footer } from 'src/shared/components/Footer'

interface ContactFormData {
  name: string
  email: string
  message: string
}

const CONTACT_PATHS = [
  {
    icon: Mail,
    wellClass: 'bg-amber-50',
    iconClass: 'text-amber-700',
    title: { en: 'Email the team', sv: 'Mejla teamet' },
    detail: CONTACT_EMAIL,
    href: `mailto:${CONTACT_EMAIL}`,
  },
  {
    icon: Calendar,
    wellClass: 'bg-stone-100',
    iconClass: 'text-stone-600',
    title: { en: 'Book a meeting', sv: 'Boka ett möte' },
    detail: {
      en: 'Pick a time that works',
      sv: 'Välj en tid som passar',
    },
    href: BOOKING_URL,
  },
  {
    icon: Clock,
    wellClass: 'bg-violet-50',
    iconClass: 'text-violet-700',
    title: { en: 'Typical reply', sv: 'Vanlig svarstid' },
    detail: { en: 'Within one hour', sv: 'Inom en timme' },
  },
] as const

export const ContactPage = () => {
  const { t } = useLocalization()
  const contact = useContact()
  const [isSubmitted, setIsSubmitted] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    mode: 'onBlur',
  })

  const onSubmit = async (data: ContactFormData) => {
    try {
      await contact.mutateAsync({
        name: data.name,
        email: data.email,
        message: data.message,
      })
      setIsSubmitted(true)
      reset()
      setTimeout(() => setIsSubmitted(false), 10000)
    } catch {
      // Error handling is done in the useContact hook
    }
  }

  return (
    <Column className="relative">
      <Column className="relative box-border w-full bg-white p-4 md:p-6">
        <Column className="relative mx-auto w-full max-w-6xl overflow-hidden rounded-[32px] border border-stone-300/80 bg-[linear-gradient(165deg,#fbfbfc_0%,#f4f5f7_38%,#eef0f3_72%,#e8eaee_100%)] px-4 py-10 md:rounded-[44px] md:px-8 md:py-14">
          <div
            className="absolute inset-0 [background-image:linear-gradient(to_right,rgba(0,0,0,0.025)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.025)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(circle_at_50%_42%,black,transparent_72%)] [-webkit-mask-image:radial-gradient(circle_at_50%_42%,black,transparent_72%)]"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            aria-hidden
          >
            <div className="absolute size-[420px] rounded-full border border-stone-200/70 md:size-[560px]" />
            <div className="absolute size-[580px] rounded-full border border-stone-200/50 md:size-[760px]" />
            <div className="absolute size-[740px] rounded-full border border-stone-200/35 md:size-[960px]" />
          </div>

          <Row className="relative z-10 w-full !flex-col items-stretch gap-10 md:!flex-row md:gap-12">
            <Column className="w-full justify-center gap-8 md:w-[42%] md:min-w-[16rem]">
              <Column className="gap-4">
                <h1 className="font-inter m-0 text-4xl font-normal !leading-[1.12] md:text-5xl">
                  <span className="bg-gradient-to-b from-black to-neutral-600 bg-clip-text text-transparent">
                    {t('Talk to the Klayder team', 'Prata med Klayder-teamet')}
                  </span>
                </h1>
                <p className="m-0 max-w-md text-lg !font-normal tracking-tight text-neutral-700 md:text-xl">
                  {t(
                    'Have a merch project coming up? Write to us and a person at Klayder will get back to you, typically within an hour.',
                    'Har ni ett merchprojekt på gång? Skriv till oss så återkommer en person på Klayder, vanligtvis inom en timme.'
                  )}
                </p>
              </Column>

              <Column className="gap-2">
                {CONTACT_PATHS.map((path) => {
                  const Icon = path.icon
                  const detail =
                    typeof path.detail === 'string'
                      ? path.detail
                      : t(path.detail.en, path.detail.sv)
                  const content = (
                    <Row className="items-center gap-3">
                      <Column
                        className={cn(
                          'size-10 shrink-0 items-center justify-center rounded-xl',
                          path.wellClass
                        )}
                      >
                        <Icon
                          className={cn('size-5', path.iconClass)}
                          strokeWidth={1.75}
                        />
                      </Column>
                      <Column className="min-w-0 flex-1 gap-0.5">
                        <p className="m-0 text-base font-normal tracking-tight text-neutral-900">
                          {t(path.title.en, path.title.sv)}
                        </p>
                        <p className="m-0 truncate text-sm text-neutral-500">
                          {detail}
                        </p>
                      </Column>
                      {'href' in path && (
                        <ArrowUpRight
                          className="size-4 shrink-0 text-neutral-400"
                          strokeWidth={1.75}
                        />
                      )}
                    </Row>
                  )

                  if ('href' in path) {
                    return (
                      <a
                        key={path.title.en}
                        href={path.href}
                        {...(path.href.startsWith('http')
                          ? { target: '_blank', rel: 'noopener noreferrer' }
                          : {})}
                        className="rounded-[22px] border border-transparent bg-white/70 px-4 py-3.5 transition-all duration-200 hover:border-stone-200 hover:bg-white"
                      >
                        {content}
                      </a>
                    )
                  }

                  return (
                    <div
                      key={path.title.en}
                      className="rounded-[22px] border border-transparent bg-white/70 px-4 py-3.5"
                    >
                      {content}
                    </div>
                  )
                })}
              </Column>
            </Column>

            <Column className="min-w-0 flex-1 justify-center">
              <Column className="gap-6 rounded-[24px] bg-white px-6 py-8 shadow-soft md:rounded-[28px] md:px-8 md:py-10">
                {isSubmitted ? (
                  <Column className="items-center justify-center gap-4 py-10 text-center">
                    <Column className="size-14 items-center justify-center rounded-2xl bg-emerald-50">
                      <CheckCircle2
                        className="size-6 text-emerald-700"
                        strokeWidth={1.75}
                      />
                    </Column>
                    <Column className="max-w-sm items-center gap-2">
                      <h2 className="m-0 text-2xl font-normal tracking-tight text-neutral-900">
                        {t('Message sent', 'Meddelande skickat')}
                      </h2>
                      <p className="m-0 text-neutral-600">
                        {t(
                          "We've received your message and will get back to you within an hour.",
                          'Vi har tagit emot ditt meddelande och återkommer inom en timme.'
                        )}
                      </p>
                    </Column>
                    <Button
                      variant="secondary"
                      radius="full"
                      onClick={() => setIsSubmitted(false)}
                    >
                      {t('Send another message', 'Skicka ett till meddelande')}
                    </Button>
                  </Column>
                ) : (
                  <>
                    <Column className="gap-1">
                      <h2 className="m-0 text-2xl font-normal tracking-tight text-neutral-900">
                        {t('Send a message', 'Skicka ett meddelande')}
                      </h2>
                      <p className="m-0 text-neutral-500">
                        {t(
                          'Tell us what you need. We will take it from there.',
                          'Berätta vad ni behöver. Vi tar det därifrån.'
                        )}
                      </p>
                    </Column>

                    <form onSubmit={handleSubmit(onSubmit)}>
                      <Column className="gap-5">
                      <Row className="w-full !flex-col items-stretch gap-5 md:!flex-row">
                        <Column className="w-full gap-1 md:flex-1">
                          <Input
                            {...register('name', {
                              required: t('Name is required', 'Namn krävs'),
                              minLength: {
                                value: 2,
                                message: t(
                                  'Name must be at least 2 characters',
                                  'Namnet måste vara minst 2 tecken'
                                ),
                              },
                            })}
                            placeholder={t('Your name', 'Ditt namn')}
                            label={t('Name', 'Namn')}
                            className="w-full"
                            fullWidth
                          />
                          {errors.name && (
                            <p className="ml-2 text-xs text-red-500">
                              {errors.name.message}
                            </p>
                          )}
                        </Column>

                        <Column className="w-full gap-1 md:flex-1">
                          <Input
                            {...register('email', {
                              required: t(
                                'Email is required',
                                'E-post krävs'
                              ),
                              pattern: {
                                value:
                                  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t(
                                  'Invalid email address',
                                  'Ogiltig e-postadress'
                                ),
                              },
                            })}
                            type="email"
                            placeholder={t(
                              'you@company.com',
                              'du@foretag.se'
                            )}
                            label={t('Email', 'E-post')}
                            className="w-full"
                            fullWidth
                          />
                          {errors.email && (
                            <p className="ml-2 text-xs text-red-500">
                              {errors.email.message}
                            </p>
                          )}
                        </Column>
                      </Row>

                      <Column className="gap-1">
                        <Textarea
                          {...register('message', {
                            required: t(
                              'Message is required',
                              'Meddelande krävs'
                            ),
                            minLength: {
                              value: 10,
                              message: t(
                                'Message must be at least 10 characters',
                                'Meddelandet måste vara minst 10 tecken'
                              ),
                            },
                          })}
                          placeholder={t(
                            'Tell us about your project, questions, or how we can help...',
                            'Berätta om ert projekt, frågor eller hur vi kan hjälpa...'
                          )}
                          label={t('Message', 'Meddelande')}
                          rows={6}
                          className="w-full"
                        />
                        {errors.message && (
                          <p className="ml-2 text-xs text-red-500">
                            {errors.message.message}
                          </p>
                        )}
                      </Column>

                      <Button
                        type="submit"
                        size="lg"
                        radius="full"
                        variant="primaryOrange"
                        isLoading={contact.isPending}
                        disabled={contact.isPending}
                        className="w-full"
                      >
                        {contact.isPending
                          ? t('Sending...', 'Skickar...')
                          : t('Send message', 'Skicka meddelande')}
                        {!contact.isPending && (
                          <Send className="ml-2 size-4" strokeWidth={1.75} />
                        )}
                      </Button>

                      <Row className="items-start justify-center gap-1.5 text-center">
                        <Lock
                          className="mt-0.5 size-3 shrink-0 text-neutral-400"
                          strokeWidth={1.75}
                        />
                        <p className="m-0 text-xs text-neutral-500">
                          {t(
                            'We use your details to reply. See our ',
                            'Vi använder era uppgifter för att svara. Se vår '
                          )}
                          <Link
                            to={paths.privacyPolicy}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2 hover:text-neutral-800"
                          >
                            {t('privacy policy', 'integritetspolicy')}
                          </Link>
                          .
                        </p>
                      </Row>
                      </Column>
                    </form>
                  </>
                )}
              </Column>
            </Column>
          </Row>
        </Column>
      </Column>

      <Footer />
    </Column>
  )
}
